import React, { Fragment } from 'react'
import { object, func, number, bool } from 'prop-types'
import { useSelector } from 'react-redux'
import cabinPlaceholder from '../../assets/images/placeholders/cabin.jpg'
import Counter from '../buttons/Counter'
import { selectLabels } from '../../redux/configuration/selectors'
import { formatPriceByLanguage } from '../../redux/configuration/utilities'
import { selectLanguage } from '../../redux/session/selectors'

const AccommodationCard = ({
  accommodation,
  handleCountChange,
  curDepartureCount,
  curReturnCount,
  editReturnOnly,
  content
}) => {
  const labels = useSelector(selectLabels)
  const lang = useSelector(selectLanguage)
  const title = content?.title || ''
  const caption = content?.caption || ''
  const description = content?.textBlocks || []
  const images = content?.images?.length
    ? content.images
    : [{ displayOrder: 1, path: cabinPlaceholder }]
  const displayImage = images[0]

  // Note: Client can cause 'freeCapacity' to be less than zero by adjusting
  //  capacity to be lower than the number booked...
  const departureMax = Math.max(+accommodation?.departure?.freeCapacity, 0)
  const returnMax = Math.max(+accommodation?.return?.freeCapacity, 0) // Note: Math.max(0, undefined) => NaN

  const pricing = Object.entries(accommodation).reduce((obj, [key, values]) => {
    if (key !== 'details') {
      obj[key] = values.price
    }
    return obj
  }, {})

  return (
    <div className="accommodation-card">
      {/*  LEFT */}
      <div className="details">
        <div className="header">
          <p className="title theme-title-variant">{title}</p>
          {description.map(block => (
            <p className="description" key={block}>
              {block}
            </p>
          ))}
        </div>

        <div className="counters">
          {accommodation.departure && !editReturnOnly && (
            <div className="counter-container">
              <p className="label theme-title-variant-eight">
                {labels.departure}
              </p>
              {departureMax === 0 ? (
                <p className="no-accommodation">{labels.noCabinsOnDeparture}</p>
              ) : (
                <Fragment>
                  <Counter
                    value={curDepartureCount}
                    handleIncrement={() =>
                      handleCountChange(accommodation, 'departure', 1)
                    }
                    handleDecrement={() =>
                      handleCountChange(accommodation, 'departure', -1)
                    }
                    disableIncrement={
                      departureMax && curDepartureCount === +departureMax
                    }
                    disableDecrement={curDepartureCount === 0}
                  />
                  {pricing?.departure && (
                    <div className="price">
                      <p className="amount theme-title-variant-eight">
                        {formatPriceByLanguage(pricing?.departure, lang)}
                      </p>
                      <p className="disclaimer theme-content-variant">
                        {caption}
                      </p>
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
          {accommodation.return && (
            <div className="counter-container">
              <p className="label theme-title-variant-eight">{labels.return}</p>
              {returnMax === 0 ? (
                <p className="no-accommodation">{labels.noCabinsOnReturn}</p>
              ) : (
                <Fragment>
                  <Counter
                    value={curReturnCount}
                    handleIncrement={() =>
                      handleCountChange(accommodation, 'return', 1)
                    }
                    handleDecrement={() =>
                      handleCountChange(accommodation, 'return', -1)
                    }
                    disableIncrement={
                      returnMax && curReturnCount === +returnMax
                    }
                    disableDecrement={curReturnCount === 0}
                  />
                  {pricing?.return && (
                    <div className="price">
                      <p className="amount theme-title-variant-eight">
                        {formatPriceByLanguage(pricing?.return, lang)}
                      </p>
                      <p className="disclaimer theme-content-variant">
                        {caption}
                      </p>
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="image-container"
        style={{ backgroundImage: `url(${displayImage.path})` }}
      >
        <img src={displayImage.path} alt={caption} />
      </div>
    </div>
  )
}

AccommodationCard.propTypes = {
  accommodation: object.isRequired,
  handleCountChange: func.isRequired,
  curDepartureCount: number.isRequired,
  curReturnCount: number.isRequired,
  editReturnOnly: bool.isRequired,
  content: object
}

export default AccommodationCard
